class PredictiveSearch extends SearchForm {
  static MIN_CHARS_FOR_PREDICTION = 4;

  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.defaultSearch = this.querySelector(".default--search");
    this.demoSearch = this.querySelector(".wbsclose");
    this.searchActiveClass = this.dataset.searchActiveClass;
    this.setupEventListeners();
  }
  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this)); 
    this.addEventListener('keydown', this.onKeydown.bind(this));
    this.querySelector(".wbsclose").addEventListener('click', this.onClick.bind(this));
  }
  getQuery() {
    return this.input.value.trim();
  }
  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.defaultSearch.classList.contains('hidden'))
      this.defaultSearch.classList.add("hidden"); 
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;
    if (!this.searchTerm.length) {
      this.abortInFlightRequest();
      this.defaultSearch.classList.remove("hidden"); //for predictive search
      this.close(true);
      return;
    }
    if (this.searchTerm.length < PredictiveSearch.MIN_CHARS_FOR_PREDICTION) {
      this.abortInFlightRequest();
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
      this.close(false);
      return;
    }
    this.getSearchResults(this.searchTerm);
  }
  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }
  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortInFlightRequest();
      this.closeResults(true);
    }
  }
  abortInFlightRequest() {
    this.abortController.abort();
    this.abortController = new AbortController();
  }
  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) {
      this.defaultSearch.classList.remove("hidden"); // for predictive search
      this.demoSearch.classList.remove("hidden");
      this.hiddenClassActivity(true);
      return;
     }
    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.hiddenClassActivity(true);
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }
  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) {
        this.defaultSearch.classList.add("hidden");
        this.demoSearch.classList.add("hidden");
        this.hiddenClassActivity(false);
        this.close();
      }
    }, 100 )
  }
  onClick() {
    setTimeout(() => {
        this.defaultSearch.classList.add("hidden");
        this.demoSearch.classList.add("hidden");
        this.hiddenClassActivity(false);
        this.close();
    })
  }
  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();
    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }
  onKeydown(event) {
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }
  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    if (!searchForTextElement) return;

    // Si no hay resultados aún (o se borraron), no hay texto que actualizar
    const currentButtonText = searchForTextElement.innerText || '';
    if (!currentButtonText) return;

    // previousTerm vacío o sin match => .match() da null y rompía con .length
    if (!previousTerm) {
      if (newTerm) searchForTextElement.textContent = `Buscar “${newTerm}”`;
      return;
    }

    try {
      const escapedTerm = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const matches = currentButtonText.match(new RegExp(escapedTerm, 'g'));
      if (!matches || matches.length !== 1) {
        if (newTerm) searchForTextElement.textContent = `Buscar “${newTerm}”`;
        return;
      }
      searchForTextElement.textContent = currentButtonText.replace(previousTerm, newTerm);
    } catch (e) {
      if (newTerm) searchForTextElement.textContent = `Buscar “${newTerm}”`;
    }
  }
  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;
    if (moveUp && !selectedElement) return;
    let selectedElementIndex = -1;
    let i = 0;
    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }
    this.statusElement.textContent = "";
    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }
    if (activeElementIndex === selectedElementIndex) return;
    const activeElement = allVisibleElements[activeElementIndex];
    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }
  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );
    if (selectedOption) selectedOption.click();
  }
  getSearchResults(searchTerm) {
  const queryKey = searchTerm.replace(" ", "-").toLowerCase();
  this.setLiveRegionLoadingState();

  // No anteponer -tag:… a la query: con operadores Shopify pierde relevancia
  // y mezcla productos sin relación (ej. Azymol al buscar "acotol").
  // delete/persistente se excluyen en predictive-search.liquid al renderizar.
  // Variantes sin/con acento (lidocaina OR lidocaína) para búsqueda accent-insensitive.
  const finalQuery = SearchForm.buildAccentInsensitiveQuery(searchTerm);

  // Cancela la petición anterior para que un resultado viejo no pise uno nuevo
  // (ej. borrar "acotol" y escribir "ibuprofeno").
  this.abortInFlightRequest();

  if (this.cachedResults[queryKey]) {
    this.renderSearchResults(this.cachedResults[queryKey], searchTerm);
    return;
  }

  const searchParams = new URLSearchParams({
    q: finalQuery,
    section_id: 'predictive-search',
    'resources[type]': 'product,collection,page,article,query',
    'resources[limit]': '10',
    // each: el límite aplica por tipo. Sin esto, artículos (ej. "ley cenabast")
    // ocupan el cupo compartido y casi no llegan productos con stock.
    'resources[limit_scope]': 'each',
    // show: para que agotados relevantes (ej. Acotol) entren en el top 10.
    // El orden "con stock primero" se aplica al renderizar (reorderUnavailableProductsLast).
    'resources[options][unavailable_products]': 'show',
    // Título, descripción y SKU/barcode (ej. 9300000000897 → Vitamina C)
    'resources[options][fields]': 'title,body,variants.sku,variants.barcode'
  });
  const requestTerm = searchTerm;
  fetch(
    `${routes.predictive_search_url}?${searchParams.toString()}`,
    { signal: this.abortController.signal }
  )
    .then((response) => {
      if (!response.ok) {
        var error = new Error(response.status);
        this.hiddenClassActivity(false);
        this.close();
        throw error;
      }
      return response.text();
    })
    .then((text) => {
      // Si el usuario ya escribió otra cosa, descartar esta respuesta
      if (this.getQuery() !== requestTerm) return;

      const resultsMarkup = new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('#shopify-section-predictive-search').innerHTML;

      this.allPredictiveSearchInstances.forEach(
        (predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        }
      );

      this.renderSearchResults(resultsMarkup, requestTerm);
    })
    .catch((error) => {
      if (error?.code === 20 || error?.name === 'AbortError') return;
      this.hiddenClassActivity(false);
      this.close();
      throw error;
    });
}
  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }
  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;
    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }
  renderSearchResults(resultsMarkup, resolvedSearchTerm = this.searchTerm) {
    if (this.getQuery() !== resolvedSearchTerm) return;

    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.reorderUnavailableProductsLast();
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.hiddenClassActivity(true);
    this.open();

    if (typeof window.scheduleApplyTopsortToPredictiveSearch === 'function') {
      window.scheduleApplyTopsortToPredictiveSearch(
        this.predictiveSearchResults,
        resolvedSearchTerm
      );
    }

    // limpiar texto visible
    const searchForText = this.querySelector('[data-predictive-search-search-for-text]');
    if (searchForText) {
      searchForText.textContent = `Buscar “${resolvedSearchTerm}”`;
    }
  }
  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }
  /**
   * Mueve productos agotados al final de la lista (con stock primero).
   * Necesario porque la API usa unavailable_products=show para no excluir
   * matches agotados relevantes del top 10.
   */
  reorderUnavailableProductsLast() {
    const list = this.querySelector('#predictive-search-results-products-list');
    if (!list) return;

    const items = Array.from(list.querySelectorAll(':scope > li.predictive-search__list-item'));
    if (items.length < 2) return;

    const available = [];
    const soldOut = [];
    for (const item of items) {
      if (item.querySelector('.price--sold-out')) {
        soldOut.push(item);
      } else {
        available.push(item);
      }
    }
    if (!soldOut.length || !available.length) return;

    for (const item of available.concat(soldOut)) {
      list.appendChild(item);
    }
  }
  open() {
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }
  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }
  closeResults(clearSearchTerm = false) {
    if (typeof window.cancelScheduleApplyTopsortToPredictiveSearch === 'function') {
      window.cancelScheduleApplyTopsortToPredictiveSearch();
    }
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.predictiveSearchResults.removeAttribute('style');
  }
  hiddenClassActivity(isAddOpration = false){
    if(!this.searchActiveClass) return;
    if(!document.body.classList.contains(this.searchActiveClass) && isAddOpration){
        document.body.classList.add(this.searchActiveClass)
    }else if(document.body.classList.contains(this.searchActiveClass) && !isAddOpration){
        document.body.classList.remove(this.searchActiveClass)
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);

/**
 * Topsort en búsqueda predictiva (mismo asset que predictive-search.js para que el CDN
 * no devuelva 404 si falta subir un archivo nuevo al tema en Shopify).
 * @see sections/main-collection-product-grid.liquid (URL API)
 */
(function () {
  const TOPSORT_API_BASE_URL = 'https://services.farmex.cl/api/topsort';
  const TOPSORT_CREATE_AUCTIONS_URL = `${TOPSORT_API_BASE_URL}/auctions/create`;
  const TOPSORT_REPORT_EVENTS_URL = `${TOPSORT_API_BASE_URL}/events/report`;
  const TOPSORT_LAST_BIDS_STORAGE_KEY = 'topsort:lastResolvedBidIds';
  const TOPSORT_BIDS_HISTORY_STORAGE_KEY = 'topsort:resolvedBidIdsHistory';
  const TOPSORT_LAST_ATTRIBUTION_STORAGE_KEY = 'topsort:lastAttribution';
  const TOPSORT_ATTRIBUTION_HISTORY_STORAGE_KEY = 'topsort:attributionHistory';
  const TOPSORT_LEGACY_OPAQUE_USER_ID_LS_KEY = 'topsort:opaqueUserId';
  const TOPSORT_THEME_OPAQUE_USER_ID_LS_KEY = 'opaqueUserId';

  /** Más de 4 caracteres → mínimo 5. La subasta Topsort no corre con consultas más cortas. */
  const TOPSORT_PREDICTIVE_MIN_QUERY_CHARS = 5;
  /** Consultar Topsort solo tras dejar de escribir (ms). */
  const TOPSORT_PREDICTIVE_DEBOUNCE_MS = 400;

  let topsortPredictiveDebounceTimer = null;

  const topsortPredictiveSentImpressionBidIds = new Set();
  const topsortPredictiveSentClickActionKeys = new Set();

  function normalizeIdForAuction(rawId) {
    const value = String(rawId || '').trim();
    if (!value) return '';
    const gidMatch = value.match(/\/Product\/(\d+)$/i);
    if (gidMatch) return gidMatch[1];
    const prefixedMatch = value.match(/^p_([a-z0-9_-]+)$/i);
    if (prefixedMatch) return prefixedMatch[1];
    const numericMatch = value.match(/^(\d+)$/);
    if (numericMatch) return numericMatch[1];
    return value;
  }

  function getAuctionIdCandidates(rawId) {
    const value = String(rawId || '').trim();
    if (!value) return [];
    const normalized = normalizeIdForAuction(value);
    const candidates = [value, normalized];
    if (/^p_/i.test(value)) {
      candidates.push(value.replace(/^p_/i, ''));
    } else {
      candidates.push(`p_${value}`);
    }
    if (normalized && /^p_/i.test(normalized)) {
      candidates.push(normalized.replace(/^p_/i, ''));
    } else if (normalized) {
      candidates.push(`p_${normalized}`);
    }
    return [...new Set(candidates.map((id) => String(id || '').trim()).filter(Boolean))];
  }

  function findProductNodeByAuctionId(nodeByProductId, rawWinnerId) {
    const winnerCandidates = getAuctionIdCandidates(rawWinnerId);
    for (const candidate of winnerCandidates) {
      const node = nodeByProductId.get(candidate);
      if (node) return node;
    }
    return null;
  }

  function findWinnerBySponsoredId(winnerById, sponsoredId) {
    const candidates = getAuctionIdCandidates(sponsoredId);
    for (const candidate of candidates) {
      const winner = winnerById.get(candidate);
      if (winner) return winner;
    }
    return null;
  }

  function generateTopsortOpaqueUserIdFallback() {
    var suffix = '';
    if (window.crypto && typeof crypto.getRandomValues === 'function') {
      var bytes = new Uint8Array(8);
      crypto.getRandomValues(bytes);
      for (var i = 0; i < bytes.length; i++) {
        suffix += (bytes[i] % 36).toString(36);
      }
    } else {
      suffix = Math.random().toString(36).slice(2, 12);
    }
    return 'anon_' + Date.now() + '_' + suffix;
  }

  function promoteOpaqueUserIdToThemeKeys(value) {
    const id = String(value || '').trim();
    if (!id) return;
    try {
      localStorage.setItem(TOPSORT_THEME_OPAQUE_USER_ID_LS_KEY, id);
      localStorage.setItem(TOPSORT_LEGACY_OPAQUE_USER_ID_LS_KEY, id);
      window.opaqueUserId = id;
      document.cookie =
        TOPSORT_THEME_OPAQUE_USER_ID_LS_KEY + '=' + id + '; path=/; max-age=31536000';
    } catch (error) {
      try {
        window.opaqueUserId = id;
      } catch (e2) {
        // ignore
      }
    }
  }

  function getTopsortOpaqueUserId() {
    let themeId = '';
    let legacyId = '';
    try {
      themeId = String(window.opaqueUserId || '').trim();
      if (!themeId) {
        themeId = String(localStorage.getItem(TOPSORT_THEME_OPAQUE_USER_ID_LS_KEY) || '').trim();
      }
    } catch (error) {
      // ignore
    }
    try {
      legacyId = String(localStorage.getItem(TOPSORT_LEGACY_OPAQUE_USER_ID_LS_KEY) || '').trim();
    } catch (error) {
      // ignore
    }
    if (legacyId && themeId && legacyId !== themeId) {
      promoteOpaqueUserIdToThemeKeys(legacyId);
      return legacyId;
    }
    if (themeId) return themeId;
    if (legacyId) {
      promoteOpaqueUserIdToThemeKeys(legacyId);
      return legacyId;
    }
    const generated = generateTopsortOpaqueUserIdFallback();
    promoteOpaqueUserIdToThemeKeys(generated);
    return generated;
  }

  function getOccurredAtIso() {
    return new Date().toISOString();
  }

  function buildTopsortEvent(resolvedBidId) {
    const bidId = String(resolvedBidId || '').trim();
    if (!bidId) return null;
    const opaqueUserId = getTopsortOpaqueUserId();
    return {
      id: bidId,
      opaqueUserId: opaqueUserId,
      resolvedBidId: bidId,
      occurredAt: getOccurredAtIso()
    };
  }

  function topsortFetchCreateAuctions(payload) {
    return fetch(TOPSORT_CREATE_AUCTIONS_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      priority: 'high'
    });
  }

  async function reportTopsortEvents(payload) {
    if (!payload || typeof payload !== 'object') return;
    const hasImpressions = Array.isArray(payload.impressions) && payload.impressions.length > 0;
    const hasClicks = Array.isArray(payload.clicks) && payload.clicks.length > 0;
    if (!hasImpressions && !hasClicks) return;
    try {
      const response = await fetch(TOPSORT_REPORT_EVENTS_URL, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) {
        const responseText = await response.text();
        console.warn('[TOPSORT predictive] report events failed', response.status, responseText);
        return;
      }
      console.log('[TOPSORT predictive] report events sent', payload);
    } catch (error) {
      console.warn('[TOPSORT predictive] report events error', error);
    }
  }

  function saveResolvedBidIdsForPredictive(nodes, searchQuery) {
    if (!Array.isArray(nodes) || !nodes.length) return;
    const attributionPairs = [];
    for (const node of nodes) {
      if (!node || node.getAttribute('data-sponsored') !== 'true') continue;
      const resolvedBidId = String(node.getAttribute('data-resolved-bid-id') || '').trim();
      if (!resolvedBidId) continue;
      const campaignId = String(node.getAttribute('data-campaign-id') || '').trim();
      attributionPairs.push({
        resolvedBidId: resolvedBidId,
        campaignId: campaignId || null
      });
    }
    const uniqueByResolvedBid = [];
    const seenResolvedBidIds = new Set();
    for (const pair of attributionPairs) {
      if (!pair?.resolvedBidId || seenResolvedBidIds.has(pair.resolvedBidId)) continue;
      seenResolvedBidIds.add(pair.resolvedBidId);
      uniqueByResolvedBid.push(pair);
    }
    if (!uniqueByResolvedBid.length) return;

    const visitRecord = {
      occurredAt: getOccurredAtIso(),
      path: window.location.pathname + window.location.search,
      collectionHandle: 'predictive-search',
      searchQuery: String(searchQuery || '').trim(),
      resolvedBidIds: uniqueByResolvedBid.map((item) => item.resolvedBidId),
      attribution: uniqueByResolvedBid
    };

    try {
      localStorage.setItem(TOPSORT_LAST_BIDS_STORAGE_KEY, JSON.stringify(visitRecord));
      localStorage.setItem(TOPSORT_LAST_ATTRIBUTION_STORAGE_KEY, JSON.stringify(visitRecord.attribution));
      const currentHistoryRaw = localStorage.getItem(TOPSORT_BIDS_HISTORY_STORAGE_KEY);
      const parsedHistory = JSON.parse(currentHistoryRaw || '[]');
      const currentHistory = Array.isArray(parsedHistory) ? parsedHistory : [];
      currentHistory.unshift(visitRecord);
      localStorage.setItem(TOPSORT_BIDS_HISTORY_STORAGE_KEY, JSON.stringify(currentHistory.slice(0, 30)));

      const currentAttributionHistoryRaw = localStorage.getItem(TOPSORT_ATTRIBUTION_HISTORY_STORAGE_KEY);
      const parsedAttributionHistory = JSON.parse(currentAttributionHistoryRaw || '[]');
      const currentAttributionHistory = Array.isArray(parsedAttributionHistory) ? parsedAttributionHistory : [];
      currentAttributionHistory.unshift({
        occurredAt: visitRecord.occurredAt,
        path: visitRecord.path,
        collectionHandle: visitRecord.collectionHandle,
        searchQuery: visitRecord.searchQuery,
        attribution: visitRecord.attribution
      });
      localStorage.setItem(
        TOPSORT_ATTRIBUTION_HISTORY_STORAGE_KEY,
        JSON.stringify(currentAttributionHistory.slice(0, 30))
      );
      window.topsortLastResolvedBidIds = visitRecord;
      window.topsortLastAttribution = visitRecord.attribution;
    } catch (error) {
      console.warn('[TOPSORT predictive] unable to save resolvedBidIds', error);
    }
  }

  function reportSponsoredImpressions(nodes) {
    if (!Array.isArray(nodes) || !nodes.length) return;
    const impressions = [];
    for (const node of nodes) {
      if (!node || node.getAttribute('data-sponsored') !== 'true') continue;
      const resolvedBidId = String(node.getAttribute('data-resolved-bid-id') || '').trim();
      if (!resolvedBidId) continue;
      if (topsortPredictiveSentImpressionBidIds.has(resolvedBidId)) continue;
      topsortPredictiveSentImpressionBidIds.add(resolvedBidId);
      const eventPayload = buildTopsortEvent(resolvedBidId);
      if (!eventPayload) continue;
      impressions.push(eventPayload);
    }
    if (impressions.length) {
      reportTopsortEvents({ impressions: impressions });
    }
  }

  function reportSponsoredClickAction(resolvedBidId, actionType) {
    const bidId = String(resolvedBidId || '').trim();
    const action = String(actionType || '').trim();
    if (!bidId || !action) return;
    const actionKey = `${bidId}:${action}`;
    if (topsortPredictiveSentClickActionKeys.has(actionKey)) return;
    topsortPredictiveSentClickActionKeys.add(actionKey);
    const clickEvent = buildTopsortEvent(bidId);
    if (!clickEvent) return;
    reportTopsortEvents({ clicks: [clickEvent] });
  }

  function bindSponsoredClickEvent(node) {
    if (!node) return;
    if (node.getAttribute('data-sponsored-click-bound') === 'true') return;
    node.setAttribute('data-sponsored-click-bound', 'true');
    node.addEventListener('click', function handleSponsoredClick(event) {
      const target = event.target instanceof Element ? event.target : null;
      if (!target) return;
      const resolvedBidId = String(node.getAttribute('data-resolved-bid-id') || '').trim();
      if (!resolvedBidId) return;
      const productLink = target.closest('a[href*="/products/"]');
      if (productLink) {
        reportSponsoredClickAction(resolvedBidId, 'open_product');
      }
      const addButton = target.closest('button[name="add"], .quick-add__submit, .product-form__submit');
      if (addButton) {
        reportSponsoredClickAction(resolvedBidId, 'add_to_cart');
      }
    });
    node.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
      form.addEventListener('submit', function handleSponsoredAddToCart() {
        const resolvedBidId = String(node.getAttribute('data-resolved-bid-id') || '').trim();
        if (!resolvedBidId) return;
        reportSponsoredClickAction(resolvedBidId, 'add_to_cart');
      });
    });
  }

  function ensureSponsoredBadge(productItemNode) {
    if (!productItemNode) return;
    const cardRoot =
      productItemNode.querySelector('.card-wrapper, .wbproduct-container, .card, .product-card-wrapper') ||
      productItemNode;
    cardRoot.style.position = cardRoot.style.position || 'relative';
    let badge = cardRoot.querySelector('.topsort-sponsored-badge');
    if (!badge) {
      badge = document.createElement('span');
      badge.className = 'topsort-sponsored-badge';
      badge.textContent = 'Patrocinado';
      cardRoot.prepend(badge);
    }
  }

  function removeSponsoredBadge(productItemNode) {
    if (!productItemNode) return;
    const badge = productItemNode.querySelector('.topsort-sponsored-badge');
    if (badge) badge.remove();
  }

  function schedulePredictivePostOrderingWork(sponsoredNodes, searchQuery) {
    const runPostWork = () => {
      for (const node of sponsoredNodes) {
        ensureSponsoredBadge(node);
        bindSponsoredClickEvent(node);
      }
      saveResolvedBidIdsForPredictive(sponsoredNodes, searchQuery);
      reportSponsoredImpressions(sponsoredNodes);
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(runPostWork, { timeout: 1200 });
      return;
    }
    setTimeout(runPostWork, 0);
  }

  function applyPredictiveSponsoredOrdering({
    sponsoredUnique,
    winnerById,
    productItems,
    listEl,
    nodeByProductId,
    searchQuery
  }) {
    if (!Array.isArray(sponsoredUnique) || !sponsoredUnique.length) return;
    if (!(winnerById instanceof Map)) return;
    if (!Array.isArray(productItems) || !productItems.length) return;
    if (!listEl) return;
    if (!(nodeByProductId instanceof Map)) return;

    const sponsoredNodes = [];
    const sponsoredNodeSet = new Set();

    for (const sponsoredId of sponsoredUnique) {
      const node = findProductNodeByAuctionId(nodeByProductId, sponsoredId);
      if (!node) continue;
      const winner = findWinnerBySponsoredId(winnerById, sponsoredId);
      if (winner?.resolvedBidId) {
        node.setAttribute('data-resolved-bid-id', String(winner.resolvedBidId).trim());
      }
      if (winner?.campaignId) {
        node.setAttribute('data-campaign-id', String(winner.campaignId).trim());
      }
      node.setAttribute('data-sponsored', 'true');
      node.setAttribute('data-topsort-id', sponsoredId);
      sponsoredNodes.push(node);
      sponsoredNodeSet.add(node);
    }

    if (!sponsoredNodes.length) return;

    const previousSponsoredNodes = Array.from(
      listEl.querySelectorAll('li[data-product-id][data-sponsored="true"]')
    );
    for (const node of previousSponsoredNodes) {
      if (sponsoredNodeSet.has(node)) continue;
      node.setAttribute('data-sponsored', 'false');
      node.removeAttribute('data-topsort-id');
      node.removeAttribute('data-resolved-bid-id');
      node.removeAttribute('data-campaign-id');
      removeSponsoredBadge(node);
    }

    const fragment = document.createDocumentFragment();
    for (const node of sponsoredNodes) {
      fragment.appendChild(node);
    }
    listEl.insertBefore(fragment, listEl.firstChild);

    schedulePredictivePostOrderingWork(sponsoredNodes, searchQuery);
  }

  function cancelScheduleApplyTopsortToPredictiveSearch() {
    if (topsortPredictiveDebounceTimer !== null) {
      clearTimeout(topsortPredictiveDebounceTimer);
      topsortPredictiveDebounceTimer = null;
    }
  }

  /**
   * Programa una sola consulta Topsort tras una pausa de escritura; cancela la anterior.
   * No programa si hay menos de 5 caracteres (>4).
   */
  function scheduleApplyTopsortToPredictiveSearch(containerRoot, searchQuery) {
    cancelScheduleApplyTopsortToPredictiveSearch();
    const trimmed = String(searchQuery || '').trim();
    if (trimmed.length < TOPSORT_PREDICTIVE_MIN_QUERY_CHARS) {
      return;
    }

    topsortPredictiveDebounceTimer = setTimeout(() => {
      topsortPredictiveDebounceTimer = null;
      if (!containerRoot || !containerRoot.isConnected) return;

      const predictiveHost = containerRoot.closest('predictive-search');
      const liveTerm =
        predictiveHost && typeof predictiveHost.getQuery === 'function'
          ? String(predictiveHost.getQuery()).trim()
          : '';
      if (liveTerm !== trimmed) return;

      applyTopsortToPredictiveSearch(containerRoot, trimmed);
    }, TOPSORT_PREDICTIVE_DEBOUNCE_MS);
  }

  async function applyTopsortToPredictiveSearch(containerRoot, searchQuery) {
    if (!containerRoot || typeof searchQuery !== 'string') return;

    const searchQueryTrimmed = searchQuery.trim();
    if (searchQueryTrimmed.length < TOPSORT_PREDICTIVE_MIN_QUERY_CHARS) return;

    const listEl = containerRoot.querySelector('#predictive-search-results-products-list');
    if (!listEl) return;

    const productItems = Array.from(listEl.querySelectorAll('li.predictive-search__list-item[data-product-id]'));
    if (!productItems.length) return;

    const sessionOpaqueUserId = getTopsortOpaqueUserId();

    const renderedProductIds = productItems
      .map((item) => (item.getAttribute('data-product-id') || '').trim())
      .filter(Boolean);

    const uniqueRenderedIds = [...new Set(renderedProductIds)]
      .map(normalizeIdForAuction)
      .filter(Boolean);
    if (!uniqueRenderedIds.length) return;

    const uniqueProductIds = uniqueRenderedIds;
    const slots = Math.min(5, uniqueProductIds.length);

    const requestPayload = {
      opaqueUserId: sessionOpaqueUserId,
      auctions: [
        {
          opaqueUserId: sessionOpaqueUserId,
          type: 'listings',
          slots: slots,
          products: { ids: uniqueProductIds },
          searchQuery: searchQueryTrimmed
        }
      ]
    };

    const nodeByProductId = new Map();
    for (const item of productItems) {
      const itemProductId = item.getAttribute('data-product-id') || '';
      const candidateIds = getAuctionIdCandidates(itemProductId);
      for (const id of candidateIds) {
        if (!id || nodeByProductId.has(id)) continue;
        nodeByProductId.set(id, item);
      }
    }

    try {
      console.log('[TOPSORT predictive] createAuctions', {
        idsCount: uniqueProductIds.length,
        slots,
        searchQuery: searchQueryTrimmed
      });

      const response = await topsortFetchCreateAuctions(requestPayload);
      if (!response.ok) {
        console.warn('[TOPSORT predictive] createAuctions HTTP', response.status);
        return;
      }

      const data = await response.json();
      window.topsortLastPredictiveCreateAuctionsResponse = data;

      if (!containerRoot.isConnected) return;
      const predictiveHostAfterFetch = containerRoot.closest('predictive-search');
      const liveTermAfterFetch =
        predictiveHostAfterFetch && typeof predictiveHostAfterFetch.getQuery === 'function'
          ? String(predictiveHostAfterFetch.getQuery()).trim()
          : '';
      if (liveTermAfterFetch !== searchQueryTrimmed) return;

      const results = Array.isArray(data?.results) ? data.results : [];

      console.log('[TOPSORT predictive] createAuctions — respuesta JSON completa', data);
      results.forEach((r, idx) => {
        console.log(`[TOPSORT predictive] results[${idx}] — winners (raw)`, r && r.winners, {
          error: r && r.error,
          auctionId: r && r.auctionId
        });
      });

      const sponsoredIds = [];
      const winnerById = new Map();
      for (const result of results) {
        if (!result || result.error === true) continue;
        if (!Array.isArray(result.winners)) continue;
        for (const winner of result.winners) {
          if (winner?.type === 'product' && winner?.id) {
            const winnerId = String(winner.id).trim();
            sponsoredIds.push(winnerId);
            for (const key of getAuctionIdCandidates(winnerId)) {
              if (!winnerById.has(key)) winnerById.set(key, winner);
            }
          }
        }
      }

      const sponsoredUnique = [...new Set(sponsoredIds)].filter(Boolean);
      const winnersForLog = sponsoredUnique.map((id) => ({
        id,
        winner: findWinnerBySponsoredId(winnerById, id)
      }));
      console.log('[TOPSORT predictive] ganadores tipo producto (ids únicos + objeto winner)', winnersForLog);

      if (!sponsoredUnique.length) {
        console.log('[TOPSORT predictive] no sponsored winners');
        return;
      }

      applyPredictiveSponsoredOrdering({
        sponsoredUnique,
        winnerById,
        productItems,
        listEl,
        nodeByProductId,
        searchQuery: searchQueryTrimmed
      });
    } catch (error) {
      console.warn('[TOPSORT predictive] auction failed', error);
    }
  }

  window.scheduleApplyTopsortToPredictiveSearch = scheduleApplyTopsortToPredictiveSearch;
  window.cancelScheduleApplyTopsortToPredictiveSearch = cancelScheduleApplyTopsortToPredictiveSearch;
  window.applyTopsortToPredictiveSearch = applyTopsortToPredictiveSearch;
})();